<template>
    <b-card class="rounded" bg-variant="lighter">
      <div class="my-4">
        <cool-label>
          Date Range
        </cool-label>
        <date-picker :date="settings.date" :timezone="settings.timezone"
                     @timeZoneChange="settings.timezone = $event"></date-picker>
      </div>
      <div class="my-4">
        <cool-label>Time Interval</cool-label>
        <b-form-select @input="handleIntervalChange" v-model="settings.interval" :options="options.intervalOptions">
          <template slot="first">
            <option :value="''">No Time Split</option>
          </template>
        </b-form-select>
      </div>
      <div class="my-4">
        <cool-label>
          Report Field
        </cool-label>
        <mulselect
          class="hierarchySelect"
          :options="fieldOptionsWithDate"
          :values="settings.fields"
          :limit="fieldLimit">
        </mulselect>
      </div>
      <b-row class="my-4" v-if="mode === 'demand'">
        <b-col>
          <b-form-group
            :label="myAuctions ? 'My Auctions Only' : 'Market Wide Report'"
            description="Report my auctions or market wide">
            <toggle-button
              class="my-auto mr-4"
              :value="myAuctions"
              :sync="true"
              @change="$emit('myauctions-changed')"
              color="#2bd879">
            </toggle-button>
          </b-form-group>
        </b-col>
      </b-row>
      <div class="my-4">
        <cool-label>
          Stats
        </cool-label>
        <b-row no-gutters>
          <div @click="toggleInArray(index, settings.stats)" v-for="(item, index) in options.statOptions" :key="index"
               class="col-4 p-0">
            <b-card @mouseenter="statButtonMouseEnter(index)" @mouseleave="statButtonMouseLeave(index)"
                    ref="stat-button" bg-variant="light"
                    :class="{ 'border-primary': settings.stats.includes(index), 'hover': hoveredStats.includes(index) }"
                    class="m-1 p-2 stat-card-button no-overflow text-center cursor-pointer" no-body>
              <small :class="{ 'text-dark': settings.stats.includes(index) }" class="stat-button-text">{{item}}</small>
            </b-card>
          </div>
        </b-row>
      </div>
      <!--
      <div class="my-3">
        <cool-label>
          Quick Filters
        </cool-label>
        <b-card-group deck>
          <b-card bg-variant="light" no-body class="p-3">
            <b-form-group>
              <cool-label>
                Medium Type
              </cool-label>
              <b-form-checkbox-group stacked
                class="small text-darkish"
                v-model="settings.filters"
                :options="options.mediumTypeOptions">
              </b-form-checkbox-group>
            </b-form-group>
          </b-card>
          <b-card bg-variant="light" no-body class="p-3">
            <b-form-group>
              <cool-label>
                Device Type
              </cool-label>
              <b-form-checkbox-group stacked
                class="small text-darkish"
                v-model="settings.filters"
                :options="options.deviceTypeOptions">
              </b-form-checkbox-group>
            </b-form-group>
          </b-card>
        </b-card-group>
      </div>
      -->
      <b-btn @click="$emit('report-requested')" :disabled="!readyToSubmit" class="my-4" variant="primary">Report</b-btn>

      <b-collapse id="filterCollapse" class="mb-2">
        <br/>
        <cool-label>
          Filters
        </cool-label>
        <b-card no-body bg-variant="light" class="px-3 mb-2">
          <filter-selector :start-time="settings.date.start" :end-time="settings.date.end"
                           :filters="settings.filters"></filter-selector>
        </b-card>

        <b-form-row>
          <div
            v-for="(filters, field) in formattedFilters"
            :class="{
              'col-md-4': filters.length === 1,
              'col-md-8': filters.length === 2,
              'col-md-12': filters.length >= 3
            }"
            class="mt-2"
            :key="field">
            <cool-label>{{ options.fieldOptions[field] }}</cool-label>
            <b-form-row>
              <div
                :class="{
                  'col-md-12': filters.length === 1,
                  'col-md-6': filters.length === 2,
                  'col-md-4': filters.length >= 3
                }"
                v-for="filter in filters"
                :key="filter.field + filter.operator + filter.value">
                <b-btn
                  @click="removeFilter(filter)"
                  class="w-100 filterButton"
                  size="sm"
                  variant="outline-secondary">
                  <span v-if="filter.operator !== 'EQUAL'">
                    {{ conciseOperatorOptions[filter.operator] }}
                  </span>
                  {{ filter.value }}
                </b-btn>
              </div>
            </b-form-row>
          </div>
        </b-form-row>
      </b-collapse>
      <b-card no-body bg-variant="light" class="rounded-0 collapseButton">
        <div v-b-toggle.filterCollapse class="text-center py-2">
          <span class="when-closed small">
            Show Advanced Filters <span class="icon icon-Table_Arrow_Open text-muted"/>
          </span>
          <span class="when-opened small">
            Hide Advanced Filters <span class="icon icon-Table_Arrow_Close text-muted"/>
          </span>
        </div>
      </b-card>
    </b-card>
</template>

<style scoped>
  .stat-card-button {
    transition: height .3s ease;
  }

  .stat-card-button.no-overflow .stat-button-text {
    font-weight: 500;
    color: var(--darkish);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .stat-card-button.hover .stat-button-text {
    overflow: hidden;
    text-overflow: initial;
    white-space: normal;
  }

  .filterButton {
    white-space: normal;
  }

  .collapseButton {
    margin-right: -22px;
    margin-left: -22px;
  }

  .collapsed > .when-opened,
  :not(.collapsed) > .when-closed {
    display: none;
  }

</style>
<style>
  .hierarchySelect .btn-primary {
    margin-right: 15px !important;
    position: relative;
  }

  .hierarchySelect .btn-primary::after {
    content: "";
    color: var(--gray);
    font-size: 20px;
    right: -22px;
    top: -4px;
    font-family: icomoon;
    position: absolute;
  }

  .hierarchySelect .btn-primary:last-child::after {
    content: none;
  }

  .hierarchySelect .btn-primary:last-child {
    margin-right: none !important;
  }
</style>

<script>
    import DatePicker from '@/components/DatePicker'
    import Mulselect from '@/components/Mulselect'
    import FilterSelector from '@/reports/components/FilterSelector'
    import {getReportOptions, intervals, conciseOperatorOptions, isExchangeView} from '@/reports/components/ReportUtils'
    import {toggleInArray} from '@/components/utils'

    export default {
        props: {
            settings: {required: true, type: Object},
            myAuctions: {requied: true, type: Boolean}
        },
        data() {
            return {
                conciseOperatorOptions,
                // array that holds which stats are being hovered by user
                hoveredStats: []
            }
        },
        mounted() {
            /**
             * Set up expand-on-hover for stat buttons
             * we can't do it in vue methods because they
             * have trouble determining expanded height of element
             */
            for (let ref of this.$refs['stat-button']) {
                ref.classList.remove('no-overflow')
                //find expanded height of element
                //we can't use height: auto bc transitions
                //don't work with auto dimensions
                let height = ref.offsetHeight + 'px'
                ref.classList.add('no-overflow')
                let condensedHeight = ref.offsetHeight + 'px'
                ref.style.height = condensedHeight
                ref.addEventListener('mouseenter', () => {
                    ref.style.height = height
                })
                ref.addEventListener('mouseleave', () => {
                    ref.style.height = condensedHeight
                })
            }

            console.log(this.fieldOptionsWithDate)
        },
        methods: {
            statButtonMouseEnter(index) {
                setTimeout(() => {
                    this.hoveredStats.push(index)
                }, 100)
            },
            statButtonMouseLeave(index) {
                let ref = event.target
                setTimeout(() => {
                    this.hoveredStats.splice(this.hoveredStats.indexOf(index), 1)
                }, 100)
            },
            removeFilter(filter) {
                let index = this.settings.filters.indexOf(filter)
                if (index > -1) {
                    this.settings.filters.splice(index, 1)
                }
            },
            fieldsHasInterval() {
                if (this.settings.fields.length > 0) {
                    return this.settings.fields[0] === 'date'
                }
            },
            handleIntervalChange() {
                if (this.settings.interval) {
                    if (this.fieldsHasInterval()) {
                        //we wanna add date, but date's already there
                        //nothing more to do, the rest of the function
                        //can take the rest of the day off
                        return
                    } else {
                        if (this.settings.fields.length >= 2) {
                            this.settings.fields.pop()
                        }
                    }
                    this.addDateToFields()
                } else {
                    // all we do here is get rid of an existing interval
                    if (this.fieldsHasInterval()) {
                        this.removeDateFromFields()
                    }
                }
            },
            addDateToFields() {
                this.settings.fields.unshift('date')
            },
            removeDateFromFields() {
                this.settings.fields.shift();
            },
            toggleInArray
        },
        computed: {
            mode() {
                return this.$route.path.split("/")[1]
            },
            options() {
                return getReportOptions(this.mode, this.myAuctions)
            },
            fieldLimit () {
              return (this.settings.fields.indexOf('date') !== -1) ? 3 : 2;
            },
            //if date is in fields, give fieldsoptions a value so date display right
            //if date is not in fields, just give fieldsoptions
            fieldOptionsWithDate() {
                if (this.settings.fields.includes('date')) {
                    return Object.assign({'date': 'Date'}, this.options.fieldOptions)
                } else {
                    return this.options.fieldOptions
                }
            },
            formattedFilters() {
                let formatted = {}
                for (let filter of this.settings.filters) {
                    if (!(filter.field in formatted)) {
                        formatted[filter.field] = []
                    }
                    formatted[filter.field].push(filter)
                }
                return formatted
            },
            readyToSubmit() {
                return this.settings.fields.length !== 0
            }
        },
        watch: {
            settings: {
                handler(value) {
                    if (!this.fieldsHasInterval()) {
                        this.settings.interval = ''
                    }
                },
                deep: true
            }
        },
        components: {
            DatePicker,
            Mulselect,
            FilterSelector
        }
    }
</script>



// WEBPACK FOOTER //
// src/reports/pages/Explorer/ReportSettings.vue